"use client";

import { useEffect, useRef, useState } from "react";
import LanguageDropdown from "@/components/layout/LanguageDropdown";

const tocSections: [string, string][] = [
  ["cookie-what", "What Are Cookies?"],
  ["cookie-why", "Why Do We Use Cookies?"],
  ["cookie-control", "How Can I Control Cookies?"],
  ["cookie-types", "Types of Cookies We Use"],
  ["cookie-browser", "Control Cookies on Your Browser"],
  ["cookie-beacons", "Other Tracking Technologies"],
  ["cookie-flash", "Flash Cookies & LSOs"],
  ["cookie-advertising", "Targeted Advertising"],
  ["cookie-updates", "Updates to This Policy"],
  ["cookie-contact", "Further Information"],
];

export default function CookieTocSidebar() {
  const [activeId, setActiveId] = useState<string>("");
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let lastEl: HTMLElement | null = null;

    const applyHighlight = (el: HTMLElement) => {
      el.style.backgroundColor = "#FFE500";
      el.style.color = "#713f12";
      el.style.paddingLeft = "0.5rem";
      el.style.paddingRight = "0.5rem";
      el.style.marginLeft = "-0.5rem";
      el.style.marginRight = "-0.5rem";
      el.style.borderRadius = "0.25rem";
      el.style.width = "fit-content";
    };

    const removeHighlight = (el: HTMLElement) => {
      el.style.backgroundColor = "";
      el.style.color = "";
      el.style.paddingLeft = "";
      el.style.paddingRight = "";
      el.style.marginLeft = "";
      el.style.marginRight = "";
      el.style.borderRadius = "";
      el.style.width = "";
    };

    const onHashChange = () => {
      clearTimeout(timer);
      if (lastEl) removeHighlight(lastEl);
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el = document.getElementById(hash);
      if (!el) return;
      lastEl = el;
      applyHighlight(el);
      timer = setTimeout(() => {
        removeHighlight(el);
        lastEl = null;
      }, 5000);
    };

    window.addEventListener("hashchange", onHashChange);
    if (window.location.hash) onHashChange();
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      clearTimeout(timer);
      if (lastEl) removeHighlight(lastEl);
    };
  }, []);

  useEffect(() => {
    const headings = tocSections.map(([id]) => document.getElementById(id)).filter(Boolean) as HTMLElement[];

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );

    headings.forEach((el) => observerRef.current!.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <aside className="hidden xl:block w-72 shrink-0 order-last">
      <div className="sticky top-24">
        <p className="font-accent text-[11px] font-medium tracking-[0.18em] text-primary/70 uppercase mb-3">
          Table of Contents
        </p>
        <nav>
          <ol className="space-y-1.5 max-h-[calc(100vh-20rem)] overflow-y-auto pr-1">
            {tocSections.map(([id, label], i) => {
              const isActive = activeId === id;
              return (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={`text-[13px] transition-colors block leading-snug ${
                      isActive
                        ? "text-primary font-medium"
                        : "text-foreground/50 hover:text-foreground/90"
                    }`}
                  >
                    <span className={`mr-1 ${isActive ? "text-primary/70" : "text-foreground/30"}`}>
                      {i + 1}.
                    </span>
                    {label}
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>
        <LanguageDropdown />
      </div>
    </aside>
  );
}
